import React, { useId, useRef, useState } from "react";
import { FaEdit, FaSave, FaTimes, FaTrash } from "react-icons/fa";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import "./PayrollRulesPage.css";

export default function EditablePayrollRow({ item, companyId, collectionName, columns, onDelete, onSaved, disabled = false }) {
  const formId = useId();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const busy = useRef(false);
  const save = async event => {
    event.preventDefault();
    if (!companyId || busy.current) return;
    const data = {};
    for (const field of columns.flatMap(column => column.fields || [])) {
      const value = draft[field.key] ?? field.defaultValue ?? "";
      data[field.key] = field.type === "number" ? Number(value) : typeof value === "string" ? value.trim() : value;
      if ((field.required && (value === "" || (typeof value === "string" && !value.trim()))) || (field.type === "number" && (!Number.isFinite(data[field.key]) || data[field.key] < (field.min ?? 0) || (field.max !== undefined && data[field.key] > field.max)))) {
        setError("Kontrollera uppgifterna innan du sparar.");
        return;
      }
    }
    if (collectionName === "holidays") data.year = Number(data.date.slice(0, 4));
    busy.current = true;
    setSaving(true);
    setError("");
    try {
      data.updatedAt = new Date().toISOString();
      await updateDoc(doc(db, "users", companyId, collectionName, item.id), data);
      setDraft(null);
      onSaved?.({ ...item, ...data, action: "update" });
    } catch {
      setError("Kunde inte spara. Försök igen.");
    } finally { busy.current = false; setSaving(false); }
  };
  const fieldInput = field => {
    const change = value => { setDraft(previous => ({ ...previous, [field.key]: value })); setError(""); };
    const props = { form: formId, name: field.key, "aria-label": field.label, disabled: saving, required: field.required, value: draft[field.key] ?? field.defaultValue ?? "", onChange: event => change(event.target.value) };
    if (field.type === "days") return <div className="payroll-inline-days">{["måndag", "tisdag", "onsdag", "torsdag", "fredag", "lördag", "söndag"].map(day => <label key={day}><input type="checkbox" disabled={saving} checked={(draft[field.key] || []).includes(day)} onChange={event => change(event.target.checked ? [...(draft[field.key] || []), day] : draft[field.key].filter(value => value !== day))} />{day.charAt(0).toUpperCase() + day.slice(1)}</label>)}</div>;
    if (field.options) return <select {...props}>{field.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select>;
    if (field.type === "textarea") return <textarea {...props} rows={2} />;
    return <input {...props} type={field.type || "text"} min={field.min} max={field.max} step={field.type === "number" ? "0.01" : undefined} />;
  };
  return <tr className={draft ? "payroll-editing-row" : ""}>
    {columns.map((column, index) => <td key={index}>{draft ? <>
      {index === 0 && <form id={formId} onSubmit={save} />}
      <div className="payroll-inline-fields">{column.fields?.map(field => <React.Fragment key={field.key}>{fieldInput(field)}</React.Fragment>)}</div>
      {index === 0 && error && <p role="alert" className="payroll-inline-error">{error}</p>}
    </> : column.value}</td>)}
    <td><div className="payroll-row-actions">{draft ? <>
      <button key="save" type="submit" form={formId} className="payroll-icon-button" disabled={saving} aria-label={`Spara ${item.name}`} title="Spara"><FaSave /></button>
      <button key="cancel" type="button" className="payroll-icon-button" disabled={saving} onClick={() => { setDraft(null); setError(""); }} aria-label={`Avbryt redigering av ${item.name}`} title="Avbryt redigering"><FaTimes /></button>
    </> : <>
      <button key="edit" type="button" className="payroll-icon-button" disabled={disabled} onClick={event => { event.preventDefault(); event.stopPropagation(); setDraft({ ...item }); }} aria-label={`Redigera ${item.name}`} title="Redigera"><FaEdit /></button>
      <button key="delete" type="button" className="payroll-icon-button payroll-danger" disabled={disabled} onClick={() => onDelete(item)} aria-label={`Radera ${item.name}`} title="Radera"><FaTrash /></button>
    </>}</div></td>
  </tr>;
}
